/**
 * Illuminated title with the actual equipped GLB hero, or a pilgrim for first-time players.
 * The painted plate keeps navigation available while the actor loads or WebGPU is unavailable.
 */
import type { Screen, ScreenContext, ScreenDef, ScreenParams } from '../../shared';
import { disposeTree, h, type SettingsPort } from '../../ui';
import { plateSvg } from '../lite';
import {titleHero} from './titleHero';

const STYLE_ID = 'mc-titlepage-style';
const STYLE = `
.mc-titlepage { box-sizing: border-box; min-height: 100%; display: flex; flex-direction: column; align-items: center;
  padding: calc(var(--safe-t, 0px) + 12px) 16px calc(var(--safe-b, 0px) + 20px); gap: 10px; color: var(--mc-ink); }
.mc-titlepage__gear { position: absolute; top: calc(var(--notice-top,0px) + var(--safe-t, 0px) + 8px); right: calc(var(--safe-r, 0px) + 8px); z-index: 2;
  width: 44px; height: 44px; display: grid; place-items: center; border-radius: 50%; border: 1.5px solid var(--mc-ink);
  background: var(--mc-vellum-light); box-shadow: 0 2px 0 var(--mc-vellum-shadow); cursor: pointer; }
.mc-titlepage__gear:focus-visible, .mc-titlepage__start:focus-visible, .mc-titlepage__stage:focus-visible { outline: 3px solid var(--mc-lapis-light); outline-offset: 2px; }
.mc-titlepage__language { position:absolute;top:calc(var(--notice-top,0px) + var(--safe-t,0px) + 10px);left:12px;display:flex;z-index:3;border:1px solid #a28448;border-radius:22px;overflow:hidden;background:#fcf3dd;box-shadow:0 3px 12px #17244820; }
.mc-titlepage__language button { min-width:44px;min-height:44px;border:0;background:transparent;font:700 13px 'Alegreya Sans',sans-serif;color:#273957;cursor:pointer; }
.mc-titlepage__language button[aria-pressed=true] { background:#263e70;color:#ffe4a2; }
.mc-titlepage__stage { position: relative; flex: none; width: min(100%, 430px); height: min(420px, 47svh); min-height: 220px; cursor: pointer;
  border-radius: 6px; overflow: hidden; box-shadow: 0 2px 0 var(--mc-vellum-shadow), 0 14px 30px rgba(23,17,13,.22); -webkit-tap-highlight-color: transparent; }
.mc-titlepage__stage .mc-lite-plate, .mc-titlepage__stage svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.mc-titlepage__stage .mc-bust { transform-box: fill-box; transform-origin: 50% 100%; }
.mc-titlepage__stage.is-wobble .mc-bust { animation: mc-titlepage-wobble .5s cubic-bezier(.34,1.56,.64,1); }
.mc-titlepage__stage.is-wobble .mc-creature-portrait { transform-origin:50% 100%;animation:mc-titlepage-wobble .5s cubic-bezier(.34,1.56,.64,1); }
@keyframes mc-titlepage-wobble { 0% { transform: rotate(0); } 25% { transform: rotate(-7deg); } 55% { transform: rotate(5deg); } 100% { transform: rotate(0); } }
html[data-motion="reduced"] .mc-titlepage__stage.is-wobble .mc-bust, html[data-motion="min"] .mc-titlepage__stage.is-wobble .mc-bust { animation: none; }
html:is([data-motion="reduced"],[data-motion="min"]) .mc-titlepage__stage.is-wobble .mc-creature-portrait { animation:none; }
.mc-titlepage__text { display: flex; flex-direction: column; align-items: center; gap: 6px; width: min(100%, 430px); margin-top: auto; }
.mc-titlepage__logo { margin: 14px 0 0; font: 400 40px/1.05 'Ruslan Display', 'Alegreya SC', Georgia, serif; letter-spacing: .05em;
  color: var(--mc-rubric); text-align: center; text-shadow: 0 2px 0 rgba(212,165,55,.55); }
.mc-titlepage__rule { width: 180px; height: 0; margin: 6px 0; border-top: 1.5px solid var(--mc-gold-deep); position: relative; }
.mc-titlepage__rule::after { content: ""; position: absolute; left: 50%; top: -1px; width: 9px; height: 9px; margin: -4.5px 0 0 -4.5px;
  background: var(--mc-vermilion); transform: rotate(45deg); box-shadow: 0 0 0 2px var(--mc-vellum); }
.mc-titlepage__sub { margin: 0; font: italic 17px/1.3 Alegreya, Georgia, serif; color: var(--mc-ink-faded); text-align: center; }
.mc-titlepage__start { margin-top: 16px; width: 100%; min-height: 56px; border-radius: 6px; cursor: pointer; touch-action: manipulation;
  font: 700 19px/1 'Alegreya SC', Alegreya, Georgia, serif; letter-spacing: .04em; color: var(--mc-lead-white);
  background: var(--mc-vermilion); border: 2px solid var(--mc-gold); box-shadow: 0 3px 0 var(--mc-rubric), 0 8px 18px rgba(168,42,23,.25); }
.mc-titlepage__start:active { transform: translateY(2px); box-shadow: 0 1px 0 var(--mc-rubric); }
.mc-titlepage__peek { width: 100%; margin-top: 14px; padding-top: 10px; border-top: 1px dashed var(--mc-gold-deep); text-align: center; }
.mc-titlepage__peek p { margin: 0 0 8px; font: italic 15px/1.3 Alegreya, Georgia, serif; color: var(--mc-ink-faded); }
.mc-titlepage__verbs { display:flex;gap:16px;justify-content:center;margin:15px 0;color:var(--mc-rubric);font:700 15px 'Alegreya SC',serif; }
.mc-titlepage__peek small { font:400 12px Alegreya,serif;color:var(--mc-ink-faded); }
.mc-titlepage__peek a { display: flex; align-items: center; justify-content: center; min-height: 44px; margin: 6px 0; border-radius: 6px;
  font: 700 16px/1 'Alegreya SC', Alegreya, Georgia, serif; color: var(--mc-lapis, #1F3A8A); text-decoration: none;
  border: 1.5px solid var(--mc-gold-deep); background: var(--mc-vellum-light); }
@media (min-width: 1024px) and (min-height: 600px) {
  .mc-titlepage { flex-direction: row; justify-content: center; gap: 56px; padding: 40px 64px; }
  .mc-titlepage__stage { width: min(760px, 52vw, calc(100vh - 80px)); height: min(760px, 52vw, calc(100vh - 80px)); }
  .mc-titlepage__text { width: 380px; margin-top: 0; padding: 40px 32px 36px; box-sizing: border-box; background: var(--mc-vellum-light);
    border: 3px solid var(--mc-vermilion); outline: 2px solid var(--mc-gold); outline-offset: -10px;
    box-shadow: 0 2px 0 var(--mc-vellum-shadow), 0 18px 40px rgba(0,0,0,.35); }
  .mc-titlepage__start { width: 100%; }
  .mc-titlepage__logo { font-size: 40px; }
}
`;

const GEAR = `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8Zm8.2 4.6-.1-2.4 2-1.6-2-3.4-2.4.9a8 8 0 0 0-2.1-1.2L15.2 3h-4l-.4 2.5a8 8 0 0 0-2.1 1.2l-2.4-.9-2 3.4 2 1.6a8 8 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-.9c.6.5 1.3.9 2.1 1.2l.4 2.5h4l.4-2.5c.8-.3 1.5-.7 2.1-1.2l2.4.9 2-3.4Z" fill="none" stroke="#2B1D14" stroke-width="1.5" stroke-linejoin="round"/></svg>`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = STYLE;
  document.head.appendChild(s);
}

export const titleScreen: ScreenDef = {
  id: 'app/title',
  route: '#/title',
  aliases: ['title'],
  titleKey: 'app.screen.title',
  needs3d: true,
  kind: 'stage',
  scope: 'P0',
  parseParams: (): ScreenParams => ({}),
  create(ctx: ScreenContext): Screen {
    ensureStyle();
    const active = ctx.services.creatures.active();
    const returning = !!active;
    const en = document.documentElement.lang === 'en';
    const stage = h('div', { class: 'mc-titlepage__stage', attrs: { role: 'button', tabindex: 0, 'aria-label': ctx.t('app.title.poke') } });
    stage.innerHTML = plateSvg('stage', false, 320, 1.75);
    stage.setAttribute('aria-busy','true');
    const hero = titleHero(ctx,stage);
    let wobbleTimer: ReturnType<typeof setTimeout> | null = null;
    const poke = (): void => {
      hero.salute();
      stage.classList.remove('is-wobble');
      void stage.offsetWidth;
      stage.classList.add('is-wobble');
      if (wobbleTimer) clearTimeout(wobbleTimer);
      wobbleTimer = setTimeout(() => stage.classList.remove('is-wobble'), 600);
      (ctx as unknown as { audio?: { play(id: string): void } }).audio?.play('ui_tap');
    };
    stage.addEventListener('click', poke);
    stage.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); poke(); } });
    const start = h('button', { class: 'mc-titlepage__start', attrs: { type: 'button' } }, ctx.t(returning ? 'app.title.continue' : 'app.title.start'));
    start.addEventListener('click', () => { void ctx.router.goto(returning ? '#/life' : '#/make'); });
    const gear = h('button', { class: 'mc-titlepage__gear', attrs: { type: 'button', 'aria-label': ctx.t('app.screen.settings') } });
    gear.innerHTML = GEAR;
    gear.addEventListener('click', () => { void ctx.router.goto('#/settings'); });
    const language = h('div', { class: 'mc-titlepage__language', attrs: { 'aria-label': en ? 'Language' : 'Язык' } });
    for (const lang of ['ru', 'en'] as const) {
      const b = h('button', { attrs: { type: 'button', 'aria-pressed': String(lang === (en ? 'en' : 'ru')), 'aria-label': lang === 'ru' ? 'Русский' : 'English' } }, lang.toUpperCase());
      b.onclick = () => (ctx as ScreenContext & { settingsPort?: SettingsPort }).settingsPort?.set({ lang });
      language.append(b);
    }
    const root = h('section', { class: 'mc-titlepage', attrs: { 'aria-labelledby': 'mc-titlepage-logo' } },
      gear, language, stage,
      h('div', { class: 'mc-titlepage__text' },
        h('h1', { class: 'mc-titlepage__logo', attrs: { id: 'mc-titlepage-logo' } }, ctx.t('app.name')),
        h('div', { class: 'mc-titlepage__rule', attrs: { 'aria-hidden': 'true' } }),
        h('p', { class: 'mc-titlepage__sub' }, ctx.t('app.title.subtitle')),
        start,
        h('div', { class: 'mc-titlepage__peek' },
          h('p', {}, en ? 'A small beast. A very questionable legend.' : 'Маленький зверь. Большая сомнительная слава.'),
          h('div', { class: 'mc-titlepage__verbs' },
            h('span', {}, en ? '⚔ Duels' : '⚔ Поединки'), h('span', {}, en ? '✦ Adventures' : '✦ Странствия'), h('span', {}, en ? '♧ Cards' : '♧ Карты')),
          h('small', {}, en ? 'Your chronicle saves on this device.' : 'Летопись сохраняется на этом устройстве.'))));
    return {
      root,
      render: (ms) => hero.render(ms),
      isIdle: () => !stage.classList.contains('is-wobble'),
      debugState: () => ({ returning,hero:hero.debugState() }),
      dispose: () => { if (wobbleTimer) clearTimeout(wobbleTimer); hero.dispose(); disposeTree(root); },
    };
  },
};
